import PropTypes from 'prop-types';
import { css } from '@eti/styles';
import { price as priceUtil, string as stringUtil } from '@eti/utils';
import { useTranslation, useSiteContext } from '@eti/providers';
import { ScreenReaderPrice } from '@eti/components';
import { getAgeTypeLabel } from '../../../../../utils/travelers';
import { CartSubSectionTitle, CartRow, Price } from '../../CartComponents';

const whiteColourText = css`
  color: #fff;
`;

const cartRowWrapperStyles = css`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const isShowingPrice = (price) => price !== null;

const CartTaxesAndFees = ({
  ageType,
  dataTestId,
  marketingCarrierNames,
  markup,
  taxesAndFees,
  totalTaxesAndFees,
  vat,
}) => {
  const { market } = useSiteContext();
  const { t } = useTranslation();

  const ageTypeLabel = t(getAgeTypeLabel(ageType)).toLowerCase();
  const costLabel = t('Cart.CostPerAgeType', ageTypeLabel);
  const discountLabel = t('Cart.DiscountPerAgeType', ageTypeLabel);
  const vatLabel = marketingCarrierNames
    ? stringUtil.insertArgument(
        t('Cart.TaxesAndChargesPerAgeType.WithCarriers'),
        t(getAgeTypeLabel(ageType)).toLowerCase(),
        marketingCarrierNames,
      )
    : stringUtil.insertArgument(
        t('Cart.TaxesAndChargesPerAgeType'),
        t(getAgeTypeLabel(ageType)).toLowerCase(),
      );

  const showTaxBreakdown = (code, value) => (value !== 0 || code === 'FEE') && code !== 'BASE';

  return (
    <div data-testid={dataTestId}>
      {taxesAndFees.length > 0 ? (
        <>
          {taxesAndFees.map(
            ({ category, taxes }) =>
              taxes.length > 0 && (
                <div className={cartRowWrapperStyles} key={category}>
                  {market.code !== 'US' && (
                    <CartSubSectionTitle className={whiteColourText}>
                      {t(`Cart.TaxBreakdown.Category.${category}`)}
                    </CartSubSectionTitle>
                  )}
                  {taxes.map(
                    ({ title, code, amount: { value } }) =>
                      showTaxBreakdown(code, value) && (
                        <CartRow key={`${category}-${title}`}>
                          <span>{title}</span>
                          <span aria-hidden data-testid={`cart-travelers-${ageType}-taxOrFee`}>
                            {priceUtil.formatPrice(value)}
                          </span>
                          <ScreenReaderPrice price={priceUtil.formatPrice(value)} />
                        </CartRow>
                      ),
                  )}
                </div>
              ),
          )}
        </>
      ) : (
        <div className={cartRowWrapperStyles}>
          {isShowingPrice(vat) && (
            <CartRow>
              <span data-testid={`cart-travelers-${ageType}-vat-label`}>{vatLabel}</span>
              <Price aria-hidden data-testid={`cart-travelers-${ageType}-vat`}>
                {priceUtil.formatPrice(vat)}
              </Price>
              <ScreenReaderPrice price={priceUtil.formatPrice(vat)} />
            </CartRow>
          )}

          {isShowingPrice(markup) && (
            <CartRow>
              <span data-testid={`cart-travelers-${ageType}-markup-label`}>
                {markup >= 0 ? costLabel : discountLabel}
              </span>
              <Price aria-hidden data-testid={`cart-travelers-${ageType}-markup`}>
                {priceUtil.formatPrice(markup)}
              </Price>
              <ScreenReaderPrice price={priceUtil.formatPrice(markup)} />
            </CartRow>
          )}
        </div>
      )}
      <div className={cartRowWrapperStyles}>
        <CartRow>
          <span>{t('Cart.TotalPerAgeType', ageTypeLabel)}</span>
          <Price aria-hidden data-testid={`cart-travelers-${ageType}-totalPrice`}>
            {priceUtil.formatPrice(totalTaxesAndFees)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(totalTaxesAndFees)} />
        </CartRow>
      </div>
    </div>
  );
};

CartTaxesAndFees.propTypes = {
  ageType: PropTypes.string,
  dataTestId: PropTypes.string,
  marketingCarrierNames: PropTypes.string,
  markup: PropTypes.number,
  taxesAndFees: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      taxes: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.shape({
            currency: PropTypes.string,
            value: PropTypes.number,
          }),
          code: PropTypes.string,
          title: PropTypes.string,
        }),
      ),
    }),
  ),
  totalTaxesAndFees: PropTypes.number,
  vat: PropTypes.number,
};

export default CartTaxesAndFees;
